import { z } from "zod";

/**
 * Frequency plans a device or gateway may be registered on (ADR-089).
 *
 * A closed list, not whatever The Things Stack accepts. Argentina's licence-free
 * band is 915–928 MHz and only AU915 sub-band 2 keeps uplinks inside it; US915
 * defaults transmit near 904 MHz, outside the band (`docs/LORAWAN_INTEGRATION.md`).
 * A plan joins this list when a market where it is legal does, not before.
 *
 * Values are The Things Stack frequency plan ids, used verbatim on the wire.
 */
export const FREQUENCY_PLANS = ["AU_915_928_FSB_2"] as const;
export const frequencyPlanSchema = z.enum(FREQUENCY_PLANS, {
  message: `Choose a supported frequency plan: ${FREQUENCY_PLANS.join(", ")}.`,
});
export type FrequencyPlanId = z.infer<typeof frequencyPlanSchema>;

/**
 * Device Repository band for each plan. The repository keys profiles and
 * codecs by band, not by sub-band, so several plans can share one.
 */
export const FREQUENCY_PLAN_BAND: Readonly<Record<FrequencyPlanId, string>> = {
  AU_915_928_FSB_2: "AU_915_928",
};

/**
 * What a technician has to set on the sensor itself for each plan.
 *
 * Channel indexes follow the AU915 numbering Milesight ToolBox uses for
 * "Supported Frequency": sub-band 2 is the 125 kHz channels 8–15 plus the
 * 500 kHz channel 65.
 */
export const FREQUENCY_PLAN_DEVICE_SETTINGS: Readonly<
  Record<FrequencyPlanId, { region: string; channels: string }>
> = {
  AU_915_928_FSB_2: { region: "AU915", channels: "8-15, 65" },
};

/**
 * Whether a device or gateway here matches the Network Server (ADR-089).
 *
 * UNVERIFIED: never compared — every row registered by hand in the console
 * before provisioning existed. SYNCED: the last push or reconciliation found
 * both sides equal. FAILED: the last push failed, see `networkSyncError`.
 * DRIFT: reconciliation found the Network Server disagreeing.
 */
export const NETWORK_SYNC_STATES = ["UNVERIFIED", "SYNCED", "FAILED", "DRIFT"] as const;
export const networkSyncStateSchema = z.enum(NETWORK_SYNC_STATES);
export type NetworkSyncState = z.infer<typeof networkSyncStateSchema>;

/** Same shape and leniency as a DevEUI: read off a label or a vendor sheet. */
export const joinEuiSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s:-]/g, "").toUpperCase())
  .refine((value) => /^[0-9A-F]{16}$/.test(value), {
    message: "JoinEUI must be 16 hexadecimal characters.",
  });

/**
 * OTAA root key, entered by the technician (ADR-089).
 *
 * Forwarded to the Join Server in the request that receives it and never
 * stored, logged or echoed back. Accepts the separators a vendor sheet uses.
 */
export const appKeySchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s:-]/g, "").toUpperCase())
  .refine((value) => /^[0-9A-F]{32}$/.test(value), {
    message: "AppKey must be 32 hexadecimal characters.",
  });

/** LoRaWAN MAC versions this platform registers devices with. */
export const LORAWAN_MAC_VERSIONS = ["MAC_V1_0_2", "MAC_V1_0_3"] as const;
export type LorawanMacVersion = (typeof LORAWAN_MAC_VERSIONS)[number];

/** Regional parameters versions, as The Things Stack names them. */
export const LORAWAN_PHY_VERSIONS = ["RP001_V1_0_2_REV_B", "RP001_V1_0_3_REV_A"] as const;
export type LorawanPhyVersion = (typeof LORAWAN_PHY_VERSIONS)[number];

/**
 * How a supported model is registered on a Network Server.
 *
 * Taken from The Things Network Device Repository
 * (`TheThingsNetwork/lorawan-devices`, vendor `milesight-iot`), so the Network
 * Server gets the vendor's MAC profile and payload codec instead of a
 * hand-built one.
 */
export interface LorawanProfile {
  repository: {
    brandId: string;
    modelId: string;
    hardwareVersion: string;
    firmwareVersion: string;
  };
  macVersion: LorawanMacVersion;
  phyVersion: LorawanPhyVersion;
  supportsClassC: boolean;
  /**
   * Vendor default, overridable per device. Null until the vendor's own
   * documentation for this model states it; the technician then enters it.
   */
  defaultJoinEui: string | null;
  /** Plans the radio can be set to. Every entry must also be in FREQUENCY_PLANS. */
  frequencyPlans: readonly FrequencyPlanId[];
}
